/*
 * Copyright (c) 2026. Bolmate B.V.
 */
import './style/notificationInput.scss';

import React, {KeyboardEventHandler, MouseEventHandler} from 'react';
import {useTranslation} from 'react-i18next';

import {ChangeFunction, WithBackground} from '@dvrd/dvr-controls';

import {BolmateButton, BolmateInput, BolmateNumberInput} from '../../../controls';

import classNames from 'classnames';

interface Props<T> {
    onActivate: MouseEventHandler;
    onClose: MouseEventHandler;
    onChange: ChangeFunction<T>;
    onSubmit: MouseEventHandler & KeyboardEventHandler;
    value: T;
    parentValue: T;
    label: string;
    active: boolean;
    disabled?: boolean;
    inputProps?: Record<string, any>;
}

export default function NotificationInput<T extends number | string>(props: Props<T>) {
    const {t} = useTranslation();
    const {onActivate, onClose, onChange, onSubmit, value, label, active, inputProps, disabled, parentValue} = props;

    function renderInput() {
        if (typeof value === 'string')
            return (
                <BolmateInput
                    onChange={onChange as ChangeFunction<string>}
                    value={value}
                    onEnter={onSubmit}
                    autoFocus
                    {...inputProps}
                />
            );
        return (
            <BolmateNumberInput
                onChange={onChange as ChangeFunction<number>}
                value={value as number}
                onEnter={onSubmit}
                autoFocus
                asNumber
                {...inputProps}
            />
        );
    }

    return (
        <>
            <div
                className={classNames('var-button', disabled && 'disabled', 'products-button')}
                onClick={disabled ? undefined : onActivate}
            >
                <label className='button-label'>{parentValue}</label>
            </div>
            <WithBackground onClose={onClose} active={active} preventBackgroundClick escapeDisabled>
                <div className='default-popup notification-input'>
                    <div className='header-container'>
                        <label className='header-title'>{label}</label>
                    </div>
                    <div className='body-container'>{renderInput()}</div>
                    <div className='footer-container btn-2'>
                        <BolmateButton
                            onClick={onClose}
                            label={t('notifications.components.cancel', 'Annuleren')}
                            secondary
                        />
                        <BolmateButton onClick={onSubmit} label={t('notifications.components.confirm', 'Bevestigen')} />
                    </div>
                </div>
            </WithBackground>
        </>
    );
}
